import { Action, createReducer, on } from '@ngrx/store';
import { AppState } from './app.state';

import * as authActions from '@store/auth/auth.actions';
import * as customerActions from '@store/customer/customer.actions';
import * as offersActions from '@store/offers/offers.actions';
import * as onboardingActions from '@store/onboarding/onboarding.actions';
import * as appActions from './app.actions';

export const initialApp: AppState = {
  currentRoute: null,
  currentFlow: null,
  step: 1,
  largeContentConfig: null,
  transactions: null,
  session: null,
  isLoading: false,
  error: false,
  openMenu: false,
  modalShow: false,
  validation: null,
  redirectUrl: null,
  fastOnboarding: false,
  purchaseLessOnboarding: false,
  sessionId: null,
  previousRoute: null,
  customerValidation: null,
};

const _appReducer = createReducer(
  initialApp,
  on(offersActions.setSelectedOfferId, (state) => {
    return {
      ...state,
      isLoading: false,
      step: 1,
    };
  }),
  on(authActions.requestLoginCode, (state, { phone, resend, channel }) => {
    return {
      ...state,
      error: false,
    };
  }),
  on(appActions.getCurrentRoute, (state, { currentRoute }) => {
    return {
      ...state,
      currentRoute,
    };
  }),
  on(appActions.setCurrentFlow, (state, { currentFlow }) => {
    return {
      ...state,
      currentFlow,
    };
  }),
  on(appActions.setNewStep, (state, { step }) => {
    return {
      ...state,
      step,
    };
  }),
  on(appActions.nextStep, (state) => {
    return {
      ...state,
      step: state.step + 1,
    };
  }),
  on(appActions.setIsLargeContentConfig, (state, { largeContentConfig }) => {
    return {
      ...state,
      largeContentConfig,
    };
  }),
  on(appActions.getTransactions, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(appActions.setTransactions, (state, { transactions }) => {
    return {
      ...state,
      transactions,
    };
  }),
  on(appActions.errorTransactions, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(appActions.setIsLoading, (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(appActions.requestLogin, (state) => {
    return {
      ...state,
      isLoading: true,
      error: false,
    };
  }),
  on(appActions.successRequestLogin, (state, { session }) => {
    return {
      ...state,
      session,
      error: false,
    };
  }),
  on(appActions.failureRequestLogin, (state) => {
    return {
      ...state,
      isLoading: false,
      error: true,
    };
  }),
  on(appActions.requestCustomerValidate, (state) => {
    return {
      ...state,
      isLoading: true,
      error: false,
    };
  }),
  on(appActions.successRequestCustomerValidate, (state, { validation }) => {
    return {
      ...state,
      step: 1,
      validation,
      error: false,
    };
  }),
  on(appActions.failureRequestCustomerValidate, (state) => {
    return {
      ...state,
      isLoading: false,
      error: true,
    };
  }),
  on(appActions.closeSession, (state) => {
    return {
      ...state,
      currentRoute: null,
      currentFlow: null,
      step: 1,
      largeContentConfig: null,
      transactions: null,
      session: null,
      isLoading: false,
    };
  }),
  on(authActions.failureRequestLoginCode, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(appActions.killPurchase, (state) => {
    return {
      ...state,
      error: false,
      isLoading: true,
    };
  }),
  on(appActions.successKillPurchase, (state) => {
    return {
      ...state,
      error: false,
      session: null,
      isLoading: false,
    };
  }),
  on(appActions.failureKillPurchase, (state) => {
    return {
      ...state,
      error: true,
      isLoading: false,
    };
  }),
  on(onboardingActions.successSaveOnboardingStep, (state) => {
    return {
      ...state,
      isLoading: false,
      error: false,
    };
  }),
  on(onboardingActions.failureSaveOnboardingStep, (state) => {
    return {
      ...state,
      isLoading: false,
      error: true,
    };
  }),
  on(onboardingActions.successRequestOnboardingSession, (state) => {
    return {
      ...state,
      isLoading: false,
      error: false,
    };
  }),
  on(onboardingActions.setApprovedAuth10tixStatus, (state, { status }) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(customerActions.successRequestCustomerInformation, (state) => {
    return {
      ...state,
      step: state.step + 1,
    };
  }),
  on(onboardingActions.successSaveAuth10tixStatus, (state) => {
    return {
      ...state,
      step: state.step + 1,
      isLoading: false,
    };
  }),
  on(customerActions.updateLoading, (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(appActions.handleMenu, (state) => {
    return {
      ...state,
      openMenu: !state.openMenu,
    };
  }),
  on(appActions.closeMenu, (state) => {
    return {
      ...state,
      openMenu: false,
    };
  }),
  on(
    appActions.updateModalShow,
    (state, { modalShow }): AppState => ({
      ...state,
      modalShow,
    })
  ),
  on(
    customerActions.successListenCustomerValidation,
    (state, { validation }): AppState => ({
      ...state,
      validation,
    })
  ),
  on(appActions.killPurchaseWithoutRedirect, (state) => {
    return {
      ...state,
      error: false,
      isLoading: true,
    };
  }),
  on(appActions.successKillPurchaseWithoutRedirect, (state) => {
    return {
      ...state,
      error: false,
      session: null,
      isLoading: false,
    };
  }),
  on(appActions.failureKillPurchaseWithoutRedirect, (state) => {
    return {
      ...state,
      error: true,
      isLoading: false,
    };
  }),
  on(appActions.setRedirectUrl, (state, { redirectUrl }) => {
    return {
      ...state,
      redirectUrl,
    };
  }),
  on(appActions.setRedirectUrlSuccessFlow, (state, { redirectUrl }) => {
    return {
      ...state,
      redirectUrl,
    };
  }),
  on(appActions.validateFastOnboarding, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(appActions.setFastOnboardingFlag, (state, { fastOnboarding }) => {
    return {
      ...state,
      fastOnboarding,
    };
  }),
  on(onboardingActions.successGetPreCustomer, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(onboardingActions.resetPrecustomerError, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  //PURCHASELESS ONBOARDING

  on(appActions.setPurchaseLessOnboardingFlag, (state, { purchaseLessOnboarding }) => {
    return {
      ...state,
      purchaseLessOnboarding,
    };
  }),
  on(appActions.successSetSessionId, (state, { sessionId }) => {
    return {
      ...state,
      sessionId,
    };
  }),
  on(appActions.setPreviousRoute, (state, { previousRoute }) => {
    return {
      ...state,
      previousRoute,
    };
  }),
  on(appActions.requestCustomerValidation, (state) => {
    return {
      ...state,
      isLoading: true,
      error: false,
    };
  }),
  on(appActions.successRequestCustomerValidation, (state, { customerValidation }) => {
    return {
      ...state,
      isLoading: false,
      customerValidation,
      error: false,
    };
  }),
  on(appActions.failureRequestCustomerValidation, (state) => {
    return {
      ...state,
      isLoading: false,
      error: true,
    };
  }),
  on(appActions.checkFastOnboarding, (state) => {
    return {
      ...state,
      step: 1,
    };
  }),
  on(appActions.setCustomer, (state, { isCustomer }) => {
    return {
      ...state,
      session: state.session
        ? { ...state.session, is_customer: isCustomer }
        : { is_customer: isCustomer },
    };
  }),
  on(onboardingActions.failureSaveSecondOnboardingStep, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  })
);

export function AppReducers(state: AppState | undefined, action: Action) {
  return _appReducer(state, action);
}
